import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FileText, Receipt } from "lucide-react";
import { api, errMsg, formatIDR, formatTime, STATUS_LABEL } from "@/lib/api";
import { StatusBadge, Avatar } from "@/components/shared";
import { KtpViewer } from "@/components/KtpViewer";
import { WaNumbersTab } from "@/components/admin/WaNumbersTab";
import { AttentionPanel } from "@/components/admin/AttentionPanel";
import { WaSettingsCard } from "@/components/admin/WaSettingsCard";
import { PayoutForm } from "@/components/admin/PayoutForm";
import { FeaturedPicker } from "@/components/admin/FeaturedPicker";
import { ProofViewer } from "@/components/ProofViewer";

const TABS = [["ringkasan", "Ringkasan"], ["talent", "Talent"], ["wa", "Nomor WA"], ["order", "Order"], ["laporan", "Laporan"], ["blacklist", "Blacklist"], ["payout", "Payout"], ["notif", "Notifikasi WA"]];

function Stat({ label, value, testId }) {
  return <div className="card-soft p-5"><p className="eyebrow">{label}</p><p className="mt-2 font-heading text-2xl font-semibold text-slate-800" data-testid={testId}>{value}</p></div>;
}

function DaysOffConflicts() {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/admin/days-off-conflicts").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const reassignAll = async (g) => {
    if (!window.confirm(`Alihkan ${g.orders.length} order ${g.talent_name} di hari libur ke talent lain? Jika tidak ada talent cocok, order di-refund penuh.`)) return;
    setBusy(g.talent_id);
    try {
      const { data } = await api.post(`/admin/talents/${g.talent_id}/reassign-days-off`);
      const moved = data.results.filter((r) => r.result === "reassigned").length;
      toast.success(`${moved} order dialihkan, ${data.results.length - moved} di-refund`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  if (!list) return null;
  return (
    <div className="mt-6 card-soft p-5" data-testid="days-off-conflicts">
      <p className="eyebrow mb-3">Order di hari libur talent</p>
      {list.length === 0 && <p className="text-sm text-slate-500" data-testid="days-off-conflicts-empty">Tidak ada order yang bentrok dengan hari libur talent.</p>}
      <div className="space-y-4">
        {list.map((g) => (
          <div key={g.talent_id} className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid={`days-off-conflict-${g.talent_id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="font-medium text-slate-800">{g.talent_name} <span className="text-slate-500 font-normal text-sm">· {g.orders.length} order terdampak</span></p>
              <button onClick={() => reassignAll(g)} disabled={busy === g.talent_id} className="btn-primary h-9 px-4 text-sm" data-testid={`days-off-reassign-${g.talent_id}`}>{busy === g.talent_id ? "Mengalihkan..." : "Alihkan semua"}</button>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {g.orders.map((o) => <li key={o.id} className="flex gap-3 flex-wrap" data-testid={`days-off-conflict-order-${o.code}`}><span className="font-medium">{o.code}</span><span>{o.service_name} {o.duration_minutes}m</span><span className="text-slate-500">{formatTime(o.scheduled_at)} WIB</span><StatusBadge status={o.status} /></li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function Ringkasan() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-slate-500">Memuat...</p>;
  return (
    <div>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
        <Stat label="Pendapatan terbayar" value={formatIDR(s.revenue_paid)} testId="stat-revenue" />
        <Stat label="Talent menunggu" value={s.talents_pending} testId="stat-talents-pending" />
        <Stat label="Laporan terbuka" value={s.reports_open} testId="stat-reports-open" />
        <Stat label="Krisis belum ditangani" value={s.crisis_open} testId="stat-crisis-open" />
        <Stat label="Order di hari libur" value={s.days_off_conflicts} testId="stat-days-off-conflicts" />
      </div>
      <AttentionPanel />
      <DaysOffConflicts />
      <div className="mt-6 card-soft p-5">
        <p className="eyebrow mb-3">Order per status</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          {Object.entries(s.orders_by_status).map(([k, v]) => <div key={k} className="flex justify-between border-b border-border py-1.5"><span className="text-slate-600">{STATUS_LABEL[k] || k}</span><span className="font-medium">{v.count}</span></div>)}
        </div>
      </div>
    </div>
  );
}

function TalentTab() {
  const [list, setList] = useState([]);
  const [viewKtp, setViewKtp] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(() => api.get("/admin/talents").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (t, status) => {
    try { await api.patch(`/admin/talents/${t.id}/status`, { status, admin_note: "" }); toast.success(`Talent ${t.name}: ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = list.reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});
  const shown = filter === "all" ? list : list.filter((t) => t.status === filter);
  return (
    <div>
      <FeaturedPicker />
      <div className="flex gap-2 flex-wrap mb-6" data-testid="admin-talent-filter">
        {[["pending", "Menunggu"], ["approved", "Disetujui"], ["rejected", "Ditolak"], ["banned", "Dikeluarkan"], ["all", "Semua"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-4 h-10 rounded-full text-sm border transition-colors duration-300 ${filter === k ? "bg-primary text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`} data-testid={`admin-talent-filter-${k}`}>
            {label} ({k === "all" ? list.length : counts[k] || 0})
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-slate-500 text-sm" data-testid="admin-talent-empty">Tidak ada talent di kategori ini.</p>}
      <div className="grid gap-4 md:grid-cols-2">
      {shown.map((t) => (
        <div key={t.id} className="card-soft p-5 flex gap-4" data-testid={`admin-talent-${t.id}`}>
          <Avatar src={t.avatar_url} name={t.name} size={56} online={t.is_online} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2"><p className="font-heading font-medium text-slate-800">{t.name}</p><span className={`text-xs px-2.5 py-1 rounded-full border ${t.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : t.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`admin-talent-status-${t.id}`}>{t.status}</span></div>
            <p className="text-xs text-slate-500">{t.email}{t.whatsapp_personal && ` · WA pribadi +${t.whatsapp_personal}`}</p>
            <p className="text-xs mt-0.5" data-testid={`admin-talent-wa-${t.id}`}>{t.whatsapp_business ? <span className="text-sky-700">Nomor resmi +{t.whatsapp_business}</span> : <span className="text-amber-700">Belum ada nomor resmi{t.status === "approved" ? " — tetapkan di tab Nomor WA" : ""}</span>}</p>
            <p className="text-sm text-slate-600 mt-2 line-clamp-2">{t.bio}</p>
            <p className="text-xs text-slate-500 mt-1">Layanan: {t.service_ids?.join(", ")} · Topik: <span data-testid={`admin-talent-skills-${t.id}`}>{t.skills?.length ? t.skills.join(", ") : "-"}</span> · Jadwal: <span data-testid={`admin-talent-schedule-${t.id}`}>{t.schedule || "-"}</span></p>
            <p className="text-xs text-slate-500 mt-1">KTP: <span data-testid={`admin-talent-ktp-status-${t.id}`}>{t.has_ktp ? "sudah diunggah" : t.id_document_note || "belum ada"}</span>{t.ktp_needs_review && <span className="ml-1 text-amber-700 font-medium">· KTP baru, perlu review</span>}</p>
            <div className="mt-3 flex gap-2 flex-wrap">
              {t.has_ktp && <button className="btn-outline h-9 px-3 text-sm" onClick={() => setViewKtp(t)} data-testid={`admin-view-ktp-${t.id}`}><FileText size={14} strokeWidth={1.5} />Lihat KTP</button>}
              {t.status === "pending" && <button className="btn-primary h-9 px-3 text-sm" onClick={() => setStatus(t, "approved")} data-testid={`admin-approve-${t.id}`}>Setujui</button>}
              {["rejected", "banned"].includes(t.status) && <button className="btn-outline h-9 px-3 text-sm" onClick={() => setStatus(t, "pending")} data-testid={`admin-reopen-${t.id}`}>Tinjau ulang</button>}
              {t.status === "pending" && <button className="btn-outline h-9 px-3 text-sm" onClick={() => setStatus(t, "rejected")} data-testid={`admin-reject-${t.id}`}>Tolak</button>}
              {t.status === "approved" && <button className="btn-ghost h-9 text-sm text-rose-700 hover:bg-rose-50" onClick={() => setStatus(t, "banned")} data-testid={`admin-ban-${t.id}`}>Keluarkan</button>}
            </div>
          </div>
        </div>
      ))}
      </div>
      {viewKtp && <KtpViewer talent={viewKtp} onClose={() => setViewKtp(null)} />}
    </div>
  );
}

function OrderTab() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get("/admin/orders", { params: filter ? { status: filter } : {} }).then((r) => setList(r.data)), [filter]);
  useEffect(() => { load(); }, [load]);
  const refund = async (o) => {
    const reason = window.prompt("Alasan refund:", "Talent tidak muncul");
    if (!reason) return;
    try { await api.post(`/admin/orders/${o.id}/refund`, { reason }); toast.success("Refund dicatat"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reassign = async (o) => { try { const { data } = await api.post(`/admin/orders/${o.id}/reassign`); toast.success(data.new_talent ? `Dialihkan ke ${data.new_talent}` : "Tidak ada talent, di-refund"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const cancel = async (o) => { try { await api.post(`/admin/orders/${o.id}/cancel`); toast.success("Order dibatalkan"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const resolveCrisis = async (o) => { await api.post(`/admin/orders/${o.id}/resolve-crisis`); toast.success("Krisis ditandai selesai"); load(); };
  return (
    <div>
      <select className="input-soft h-10 w-auto mb-4" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-order-filter">
        <option value="">Semua status</option>
        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="overflow-x-auto card-soft">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500 border-b border-border"><tr><th className="p-3">Kode</th><th className="p-3">User</th><th className="p-3">Talent</th><th className="p-3">Layanan</th><th className="p-3">Harga</th><th className="p-3">Status</th><th className="p-3">Dibuat</th><th className="p-3">Aksi</th></tr></thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.id} className={`border-b border-border last:border-0 ${o.crisis_flag && !o.crisis_resolved ? "bg-rose-50/60" : ""}`} data-testid={`admin-order-${o.code}`}>
                <td className="p-3 font-medium">{o.code}{o.crisis_flag && <span className="ml-1 text-rose-600 text-xs">KRISIS</span>}</td>
                <td className="p-3">{o.nickname}<br /><span className="text-xs text-slate-400">{o.whatsapp}</span></td>
                <td className="p-3">{o.talent_name}</td>
                <td className="p-3">{o.service_name} {o.duration_minutes}m</td>
                <td className="p-3">{formatIDR(o.price)}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                <td className="p-3 text-slate-500 whitespace-nowrap">{formatTime(o.created_at)}<br /><span className="text-xs">Jadwal: {o.is_immediate ? "segera" : formatTime(o.scheduled_at)}</span></td>
                <td className="p-3 whitespace-nowrap">
                  {["paid", "accepted", "in_session", "completed", "cancelled"].includes(o.status) && o.paid_at && <button className="btn-ghost h-8 text-xs text-rose-700" onClick={() => refund(o)} data-testid={`admin-refund-${o.code}`}>Refund</button>}
                  {o.status === "awaiting_payment" && <button className="btn-ghost h-8 text-xs" onClick={() => cancel(o)} data-testid={`admin-cancel-${o.code}`}>Batalkan</button>}
                  {["paid", "accepted"].includes(o.status) && <button className="btn-ghost h-8 text-xs" onClick={() => reassign(o)} data-testid={`admin-reassign-${o.code}`}>Alihkan</button>}
                  {o.crisis_flag && !o.crisis_resolved && <button className="btn-ghost h-8 text-xs" onClick={() => resolveCrisis(o)} data-testid={`admin-resolve-crisis-${o.code}`}>Krisis ditangani</button>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">Belum ada order</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LaporanTab() {
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get("/admin/reports").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const resolve = async (r) => { await api.post(`/admin/reports/${r.id}/resolve`, { resolution: "Ditinjau admin" }); toast.success("Laporan diselesaikan"); load(); };
  const block = async (r) => { try { await api.post("/admin/blacklist", { phone_hash: r.user_phone_hash, phone_masked: r.user_phone_masked, reason: `Laporan ${r.order_code}: ${r.reason}` }); toast.success("Nomor user diblokir"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3">
      {list.length === 0 && <p className="text-slate-500 text-sm">Belum ada laporan.</p>}
      {list.map((r) => (
        <div key={r.id} className="card-soft p-5" data-testid={`admin-report-${r.id}`}>
          <div className="flex justify-between gap-3 flex-wrap"><p className="font-medium text-slate-800">{r.order_code} · dari {r.reporter === "user" ? "user" : `talent ${r.talent_name}`}</p><span className={`text-xs px-2.5 py-1 rounded-full border ${r.status === "open" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600"}`}>{r.status}</span></div>
          <p className="text-sm text-slate-700 mt-1">{r.reason}</p>
          {r.details && <p className="text-sm text-slate-500 mt-1">{r.details}</p>}
          <p className="text-xs text-slate-400 mt-2">{formatTime(r.created_at)} {r.user_phone_masked && `· user ${r.user_phone_masked}`}</p>
          {r.status === "open" && (
            <div className="mt-3 flex gap-2">
              <button className="btn-outline h-9 px-3 text-sm" onClick={() => resolve(r)} data-testid={`admin-report-resolve-${r.id}`}>Tandai selesai</button>
              {r.user_phone_hash && <button className="btn-ghost h-9 text-sm text-rose-700 hover:bg-rose-50" onClick={() => block(r)} data-testid={`admin-report-block-${r.id}`}>Blokir nomor user</button>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function BlacklistTab() {
  const [list, setList] = useState([]);
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const load = useCallback(() => api.get("/admin/blacklist").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => { e.preventDefault(); try { await api.post("/admin/blacklist", { phone, reason }); toast.success("Ditambahkan"); setPhone(""); setReason(""); load(); } catch (err) { toast.error(errMsg(err)); } };
  const remove = async (b) => { await api.delete(`/admin/blacklist/${b.id}`); load(); };
  return (
    <div>
      <form onSubmit={add} className="flex gap-2 flex-wrap mb-6">
        <input className="input-soft h-10 w-56" placeholder="Nomor WhatsApp" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="blacklist-phone-input" required />
        <input className="input-soft h-10 flex-1 min-w-[180px]" placeholder="Alasan" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="blacklist-reason-input" />
        <button className="btn-primary h-10 px-4" data-testid="blacklist-add-button">Blokir</button>
      </form>
      <div className="space-y-2">
        {list.map((b) => <div key={b.id} className="card-soft p-4 flex justify-between items-center gap-3" data-testid={`blacklist-item-${b.id}`}><div><p className="font-medium text-slate-800">{b.phone_masked}</p><p className="text-xs text-slate-500">{b.reason} · {formatTime(b.created_at)}</p></div><button className="btn-ghost h-8 text-xs" onClick={() => remove(b)} data-testid={`blacklist-remove-${b.id}`}>Hapus</button></div>)}
        {list.length === 0 && <p className="text-slate-500 text-sm">Blacklist kosong.</p>}
      </div>
    </div>
  );
}

function PayoutTab() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);
  const [proof, setProof] = useState(null);
  const load = useCallback(() => api.get("/admin/payouts").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-slate-500">Memuat...</p>;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <p className="eyebrow mb-2">Saldo talent (bagi hasil 70%)</p>
        {data.balances.map((b) => <div key={b.talent_id} className="card-soft p-4 flex justify-between items-center gap-3" data-testid={`payout-balance-${b.talent_id}`}><div><p className="font-medium text-slate-800">{b.name}</p><p className="text-xs text-slate-500">Total {formatIDR(b.earned)} · dibayar {formatIDR(b.paid_out)}</p></div><div className="text-right"><p className="font-heading font-semibold">{formatIDR(b.balance)}</p>{b.balance > 0 && <button className="btn-ghost h-8 text-xs" onClick={() => setPaying(b)} data-testid={`payout-pay-${b.talent_id}`}>Bayar</button>}</div></div>)}
      </div>
      <div>
        <p className="eyebrow mb-2">Riwayat payout</p>
        <div className="space-y-2">
          {data.history.map((h) => (
            <div key={h.id} className="card-soft p-3 text-sm flex justify-between items-center gap-3" data-testid={`payout-history-${h.id}`}>
              <span className="min-w-0"><span className="block text-slate-800">{h.talent_name ? `${h.talent_name} · ` : ""}{h.note}</span><span className="text-xs text-slate-500">{formatTime(h.created_at)}</span></span>
              <span className="flex items-center gap-2 shrink-0"><span className="font-medium">{formatIDR(h.amount)}</span>{h.has_proof ? <button className="btn-ghost h-8 text-xs" onClick={() => setProof(h)} data-testid={`payout-proof-${h.id}`}><Receipt size={13} strokeWidth={1.5} />Bukti</button> : <span className="text-xs text-slate-400" data-testid={`payout-noproof-${h.id}`}>tanpa bukti</span>}</span>
            </div>
          ))}
          {data.history.length === 0 && <p className="text-slate-500 text-sm">Belum ada payout.</p>}
        </div>
      </div>
      {paying && <PayoutForm balance={paying} onDone={() => { setPaying(null); load(); }} onClose={() => setPaying(null)} />}
      {proof && <ProofViewer payout={proof} endpoint={`/admin/payouts/${proof.id}/proof`} onClose={() => setProof(null)} />}
    </div>
  );
}

const DELIVERY = { sent: ["Terkirim", "bg-emerald-50 text-emerald-700 border-emerald-200"], failed: ["Gagal", "bg-rose-50 text-rose-700 border-rose-200"], pending: ["Mengirim", "bg-sky-50 text-sky-700 border-sky-200"], skipped: ["Dilewati", "bg-slate-100 text-slate-600 border-slate-200"], mocked: ["Mock", "bg-amber-50 text-amber-800 border-amber-200"] };

function NotifTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/notifications").then((r) => setList(r.data)); }, []);
  return (
    <div>
      <WaSettingsCard />
      <div className="space-y-2 mt-4">
        {list.map((n) => {
          const [label, tone] = DELIVERY[n.delivery?.status] || DELIVERY.mocked;
          return (
            <div key={n.id} className="card-soft p-4 text-sm" data-testid={`notif-${n.id}`}>
              <div className="flex justify-between gap-3 text-xs text-slate-500 flex-wrap"><span>ke {n.to} · {n.kind} <span className={`ml-1 px-2 py-0.5 rounded-full border ${tone}`} data-testid={`notif-delivery-${n.id}`}>{label}</span></span><span>{formatTime(n.created_at)}</span></div>
              <p className="mt-1 text-slate-700">{n.message}</p>
              {n.delivery?.error && <p className="mt-1 text-xs text-rose-700">{n.delivery.error}</p>}
            </div>
          );
        })}
        {list.length === 0 && <p className="text-slate-500 text-sm">Belum ada notifikasi.</p>}
      </div>
    </div>
  );
}

const VIEWS = { ringkasan: Ringkasan, talent: TalentTab, wa: WaNumbersTab, order: OrderTab, laporan: LaporanTab, blacklist: BlacklistTab, payout: PayoutTab, notif: NotifTab };

export default function AdminDashboard() {
  const [tab, setTab] = useState("ringkasan");
  const View = VIEWS[tab];
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="admin-dashboard">
      <p className="eyebrow mb-3">Admin</p>
      <h1 className="text-3xl tracking-tight font-medium text-slate-800">Dashboard Sudut Nadi</h1>
      <div className="mt-8 flex gap-2 flex-wrap" data-testid="admin-tabs">
        {TABS.map(([k, label]) => <button key={k} onClick={() => setTab(k)} className={`px-4 h-10 rounded-full text-sm border transition-colors duration-300 ${tab === k ? "bg-primary text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`} data-testid={`admin-tab-${k}`}>{label}</button>)}
      </div>
      <div className="mt-8"><View key={tab} /></div>
    </div>
  );
}
